import { PopupAdapter } from '../types';

export class CustomPopupAdapter implements PopupAdapter {
  private id: string;
  private formSelector: string;
  private shouldShow: () => Promise<boolean>;
  private afterShown: () => Promise<void>;
  private afterHidden: () => Promise<void>;
  private forceShow: boolean;

  constructor({
    id,
    formSelector,
    shouldShow,
    afterShown,
    afterHidden,
    forceShow,
  }: {
    id: string;
    formSelector: string;
    shouldShow?: () => Promise<boolean>;
    afterShown?: () => Promise<void>;
    afterHidden?: () => Promise<void>;
    forceShow?: boolean;
  }) {
    this.id = id;
    this.formSelector = formSelector;
    this.shouldShow = shouldShow ?? (() => Promise.resolve(true));
    this.afterShown = afterShown ?? (() => Promise.resolve());
    this.afterHidden = afterHidden ?? (() => Promise.resolve());

    this.forceShow = typeof forceShow === 'undefined' ? false : forceShow;
  }

  getRootElement(): HTMLDivElement | null {
    const startingElement = document.querySelector(this.formSelector);

    if (!startingElement) {
      return null;
    }

    // Scan all the way back up the tree until the parentElement is body
    let rootElement: HTMLDivElement = startingElement as HTMLDivElement;
    while (rootElement.parentElement && rootElement.parentElement.tagName !== 'BODY') {
      rootElement = rootElement.parentElement as HTMLDivElement;
    }

    return rootElement;
  }

  async show(): Promise<boolean> {
    const shouldShow = await this.shouldShow();

    if (!this.forceShow && !shouldShow) {
      return false;
    }

    const element = this.getRootElement();

    if (!element) return false;

    element.style.removeProperty('display');
    element.style.setProperty('display', 'block', 'important');

    await this.afterShown();

    return true;
  }

  async hide(): Promise<boolean> {
    const element = this.getRootElement();

    // Cleanup already happened elsewhere
    if (!element) return true;

    element.style.setProperty('display', 'none', 'important');

    await this.afterHidden();

    return true;
  }

  isVisible(): boolean {
    const element = this.getRootElement();
    return !!element;
  }

  getId(): string {
    return this.id;
  }

  getForceShow(): boolean {
    return this.forceShow;
  }
}

//@ts-ignore
window.CustomPopupAdapter = CustomPopupAdapter;
